import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Table,
  Icon,
  Flex,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableCaption,
  useDisclosure,
  useToast,
  IconButton,
} from "@chakra-ui/react";
import { FaPlus, FaUsers,FaEye, FaEdit, FaTrash } from "react-icons/fa";
import EditLibrarian from "./Edit_librarian";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const Table_librarian = () => {
  const { isOpen, onOpen, onClose } = useDisclosure(); // Chakra UI modal hooks
  const schoolId = sessionStorage.getItem("school_id"); // Get the school ID from sessionStorage
  const userId = sessionStorage.getItem("user_id"); // Get the user ID (admin's) from sessionStorage
  const [librarians, setLibrarians] = useState([]); // State to hold librarians list
  const [selectedLibrarian, setSelectedLibrarian] = useState(null); // State to store the selected librarian
  const [loading, setLoading] = useState(false); // Loading state
  const [errorMessage, setErrorMessage] = useState(""); // State to handle errors
  const toast = useToast(); // Chakra toast for notifications

  // Function to fetch librarians
  const fetchLibrarians = async () => {
    setLoading(true);
    try {
      const jwt = sessionStorage.getItem("jwt"); // Get the JWT from sessionStorage
      const response = await axios.get(
        `${BASE_URL}/admin/library/librarian/list_librarian.php?school_id=${schoolId}`,
        {
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      if (response.data.success) {
        setLibrarians(response.data.data); // Set the list of librarians
      } else {
        setErrorMessage(response.data.message || "Error fetching librarians");
        toast({
          title: "Error",
          description: response.data.message || "Error fetching librarians",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error fetching librarians:", error);
      setErrorMessage("Network Error: " + (error.message || error));
      toast({
        title: "Error",
        description: "Network Error: " + (error.message || error),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch librarians when component mounts
  useEffect(() => {
    fetchLibrarians();
  }, [schoolId]);

  // Handle edit action
  const handleEdit = (librarian) => {
    setSelectedLibrarian(librarian); // Set the selected librarian for editing
    onOpen(); // Open the modal for editing
  };

  // Handle delete action
  const handleDelete = async (librarianId) => {
    const jwt = sessionStorage.getItem("jwt"); // Get the JWT from sessionStorage
    try {
      const response = await axios.delete(
        `${BASE_URL}/admin/library/librarian/delete_librarian.php`,
        {
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
          data: {
            user_id: userId, // Include the user_id (admin ID) in the request body
            librarian_id: librarianId, // Send the librarian ID to delete
            school_id: schoolId,
          },
        }
      );
      if (response.data.success) {
        toast({
          title: "Deleted",
          description: "Librarian deleted successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        fetchLibrarians(); // Re-fetch librarians after delete
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Error deleting librarian",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting librarian:", error);
      toast({
        title: "Error",
        description: "Network Error: " + (error.message || error),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <div>
      <Button  colorScheme="blue" onClick={onOpen} leftIcon={<FaEye />}>
        View
      </Button>

      {/* Modal for displaying librarian details */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          // bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaUsers} boxSize={5} mr={2} />
            Librarian Details
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            {/* Table for listing librarians */}
            <Table
              sx={{
                "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
              }}
            >
              {/* <TableCaption>List of Librarians</TableCaption> */}
              <Thead>
                <Tr>
                  <Th>Teacher Name</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {librarians.map((librarian) => (
                  <Tr key={librarian.id}>
                    <Td>{librarian.teacher_name}</Td>
                    <Td>
                      {/* Buttons in a single row */}
                      <Flex align="center" gap={2}>
                        {/* Edit button */}
                        <EditLibrarian
                          librarianId={librarian.id}
                          schoolId={schoolId}
                        />
                        {/* Delete button */}
                        <IconButton
                          icon={<FaTrash />}
                          colorScheme="red"
                          onClick={() => handleDelete(librarian.id)} // Delete action
                          aria-label="Delete"
                        />
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default Table_librarian;
